import { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";
import { MIME_CATEGORIES, MIME_TYPES } from "../data/mimeTypes";

const CATEGORY_STYLES = {
  application: {
    badge: "bg-indigo-100 text-indigo-700 border-indigo-200",
    accent: "border-indigo-200",
  },
  audio: {
    badge: "bg-pink-100 text-pink-700 border-pink-200",
    accent: "border-pink-200",
  },
  font: {
    badge: "bg-amber-100 text-amber-700 border-amber-200",
    accent: "border-amber-200",
  },
  image: {
    badge: "bg-emerald-100 text-emerald-700 border-emerald-200",
    accent: "border-emerald-200",
  },
  message: {
    badge: "bg-sky-100 text-sky-700 border-sky-200",
    accent: "border-sky-200",
  },
  model: {
    badge: "bg-violet-100 text-violet-700 border-violet-200",
    accent: "border-violet-200",
  },
  multipart: {
    badge: "bg-orange-100 text-orange-700 border-orange-200",
    accent: "border-orange-200",
  },
  text: {
    badge: "bg-slate-100 text-slate-700 border-slate-200",
    accent: "border-slate-200",
  },
  video: {
    badge: "bg-rose-100 text-rose-700 border-rose-200",
    accent: "border-rose-200",
  },
};

const normaliseExtension = (term) => term.replace(/^\.+/, "").toLowerCase();

// Types whose canonical Content-Type header should carry a charset. IANA
// registers charset for the text/* tree, and a handful of application/*
// text-shaped formats commonly include it in the wild.
const CHARSET_UTF8_TYPES = new Set([
  "application/json",
  "application/ld+json",
  "application/xml",
  "application/yaml",
  "application/x-yaml",
  "application/toml",
  "application/javascript",
  "application/sql",
  "application/graphql",
  "application/manifest+json",
  "application/rss+xml",
  "application/atom+xml",
  "application/vnd.api+json",
  "application/problem+json",
]);

const formatContentTypeHeader = (mime) => {
  const withCharset =
    mime.startsWith("text/") || CHARSET_UTF8_TYPES.has(mime)
      ? `${mime}; charset=utf-8`
      : mime;
  return `Content-Type: ${withCharset}`;
};

const MimeTypeLookup = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const searchInputRef = useRef(null);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target;
      const tag = target?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target?.isContentEditable
      ) {
        return;
      }
      e.preventDefault();
      searchInputRef.current?.focus();
      searchInputRef.current?.select?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const handleSearchKeyDown = (e) => {
    if (e.key !== "Escape") return;
    if (!search) {
      e.target.blur();
      return;
    }
    e.preventDefault();
    setSearch("");
  };

  const filtered = useMemo(() => {
    const raw = search.trim().toLowerCase();
    const extQuery = normaliseExtension(raw);
    return MIME_TYPES.filter((entry) => {
      if (activeCategory !== "all" && entry.category !== activeCategory) {
        return false;
      }
      if (!raw) return true;
      if (entry.mime.toLowerCase().includes(raw)) return true;
      if (entry.summary.toLowerCase().includes(raw)) return true;
      if (entry.extensions.some((ext) => ext.toLowerCase() === extQuery)) {
        return true;
      }
      if (
        extQuery &&
        entry.extensions.some((ext) => ext.toLowerCase().includes(extQuery))
      ) {
        return true;
      }
      return false;
    });
  }, [search, activeCategory]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} ${t("copy_success")}`);
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const filterButtons = [
    { key: "all", labelKey: "mime_lookup_filter_all" },
    ...MIME_CATEGORIES.map((cat) => ({
      key: cat,
      labelKey: `mime_lookup_filter_${cat}`,
    })),
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("mime_lookup_title")}
            </h1>
            <p className="text-gray-600">{t("mime_lookup_intro")}</p>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="mime-lookup-search"
                className="text-sm font-medium text-gray-700"
              >
                {t("mime_lookup_search_label")}
              </label>
              <input
                id="mime-lookup-search"
                ref={searchInputRef}
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder={t("mime_lookup_search_placeholder")}
                aria-label={t("mime_lookup_search_label")}
                aria-keyshortcuts="/"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t("mime_lookup_search_hint")}
              </p>
              <p className="text-xs text-gray-500">
                {t("mime_lookup_shortcut_hint")}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {filterButtons.map((btn) => (
                <StandardButton
                  key={btn.key}
                  onClick={() => setActiveCategory(btn.key)}
                  type={activeCategory === btn.key ? "primary" : "secondary"}
                  variant={activeCategory === btn.key ? undefined : "outlined"}
                  size="small"
                >
                  {t(btn.labelKey)}
                </StandardButton>
              ))}
            </div>

            <div className="text-xs text-gray-500">
              {t("mime_lookup_count", { count: filtered.length })}
            </div>

            {filtered.length === 0 ? (
              <div className="bg-gray-50 border border-gray-200 text-gray-600 p-4 rounded-md text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <span>{t("mime_lookup_no_match")}</span>
                {activeCategory !== "all" && (
                  <StandardButton
                    onClick={() => setActiveCategory("all")}
                    type="secondary"
                    variant="outlined"
                    size="small"
                  >
                    {t("mime_lookup_clear_filter")}
                  </StandardButton>
                )}
              </div>
            ) : (
              <ul className="flex flex-col gap-2">
                {filtered.map((entry) => {
                  const style =
                    CATEGORY_STYLES[entry.category] ||
                    CATEGORY_STYLES.application;
                  return (
                    <li
                      key={entry.mime}
                      className={`bg-white border ${style.accent} rounded-md p-3`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                        <span
                          className={`inline-flex items-center justify-center px-2 py-1 text-xs font-semibold uppercase tracking-wide rounded border self-start ${style.badge}`}
                        >
                          {entry.category}
                        </span>
                        <div className="flex-1 min-w-0">
                          <code className="block font-mono text-sm text-gray-900 break-all">
                            {entry.mime}
                          </code>
                          {entry.extensions.length > 0 && (
                            <div className="mt-1 text-xs text-gray-600">
                              <span className="font-medium">
                                {t("mime_lookup_extensions_label")}:
                              </span>{" "}
                              <span className="font-mono">
                                {entry.extensions
                                  .map((ext) => `.${ext}`)
                                  .join(", ")}
                              </span>
                            </div>
                          )}
                          <p className="text-sm text-gray-700 mt-2">
                            {entry.summary}
                          </p>
                        </div>
                        <div className="flex flex-row sm:flex-col gap-1 shrink-0">
                          <StandardButton
                            onClick={() =>
                              copy(entry.mime, t("mime_lookup_copy_mime_label"))
                            }
                            type="secondary"
                            variant="outlined"
                            size="small"
                          >
                            {t("mime_lookup_copy_mime_button")}
                          </StandardButton>
                          {entry.extensions.length > 0 && (
                            <StandardButton
                              onClick={() =>
                                copy(
                                  `.${entry.extensions[0]}`,
                                  t("mime_lookup_copy_ext_label"),
                                )
                              }
                              type="secondary"
                              variant="outlined"
                              size="small"
                            >
                              {t("mime_lookup_copy_ext_button")}
                            </StandardButton>
                          )}
                          <StandardButton
                            onClick={() =>
                              copy(
                                formatContentTypeHeader(entry.mime),
                                t("mime_lookup_copy_header_label"),
                              )
                            }
                            type="secondary"
                            variant="outlined"
                            size="small"
                          >
                            {t("mime_lookup_copy_header_button")}
                          </StandardButton>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("mime_lookup_note_canonical")}</p>
              <p>{t("mime_lookup_note_ambiguous")}</p>
              <p>{t("mime_lookup_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default MimeTypeLookup;
